import React, { useState, useEffect } from 'react';
import { getFirestore, collection, getDocs, doc, updateDoc } from 'firebase/firestore';
import { app } from '../../firebase'; // Ensure this path is correct

import NavbarDashboard from '../Dashboard/NavbarDashboard';
import { Box, Button, Select, MenuItem } from '@mui/material';
import ResponsiveDrawer from '../Dashboard/Sidebar';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import axios from 'axios';

const generateYearOptions = () => {
  const now = new Date();
  const currentMonth = now.getMonth() + 1;
  const currentYear = now.getFullYear();
  const startYear = currentMonth >= 4 ? currentYear : currentYear - 1;

  return Array.from({ length: 4 }, (_, index) => {
    const year = startYear - index;
    return {
      value: year.toString(),
      label: `${year}-${String(year + 1).slice(-2)}`,
      firestoreKey: `tdsData${year}`,
    };
  });
};

const YEAR_OPTIONS = generateYearOptions();

const formatSelectedPreviousYear = (tdsYear) => {
  const previousYear = tdsYear?.previousYear;

  if (typeof previousYear === 'string' && previousYear.trim()) {
    const matchedYear = YEAR_OPTIONS.find(
      option =>
        option.value === previousYear ||
        option.label === previousYear ||
        option.firestoreKey === previousYear
    );

    return matchedYear?.label || previousYear;
  }

  if (typeof previousYear === 'number') {
    const matchedYear = YEAR_OPTIONS.find(option => option.value === String(previousYear));
    return matchedYear?.label || String(previousYear);
  }

  if (previousYear === true) return 'Yes';
  if (previousYear === false) return 'No';

  return 'No';
};

const FinalOtp = ({ userPermissions }) => {
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCompleted, setShowCompleted] = useState(false);

  const fetchData = async () => {
    try {
      const firestore = getFirestore(app);

      const [
        usersSnapshot,
        tdsStateSnapshot,
        tdsDetailsSnapshot,
        tdsYearSnapshot,
        tdsFormSnapshot,
        tdsRefundOtpSnapshot,
      ] = await Promise.all([
        getDocs(collection(firestore, 'users')),
        getDocs(collection(firestore, 'tds_state')),
        getDocs(collection(firestore, 'tds_details')),
        getDocs(collection(firestore, 'tds_refund_Years')),
        getDocs(collection(firestore, 'tds_forms')),
        getDocs(collection(firestore, 'tds_refund_otp')),
      ]);

      const usersData = usersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const tdsStateMap = new Map(tdsStateSnapshot.docs.map(doc => [doc.id, doc.data()]));
      const tdsDetailsMap = new Map(tdsDetailsSnapshot.docs.map(doc => [doc.id, doc.data()]));
      const tdsYearMap = new Map(tdsYearSnapshot.docs.map(doc => [doc.id, doc.data()]));
      const tdsFormMap = new Map(
        tdsFormSnapshot.docs.map(doc => {
          const data = doc.data();
          return [
            data.userId ?? doc.id,
            {
              ...data,
              timestamp: data.timestamp?.toDate(),
            },
          ];
        })
      );
      const tdsRefundOtpMap = new Map(tdsRefundOtpSnapshot.docs.map(doc => [doc.id, doc.data()]));

      // Merge data based on userId
      const mergedData = usersData.map(user => {
        const tdsState = tdsStateMap.get(user.id) || {};
        const tdsDetails = tdsDetailsMap.get(user.id) || {};
        const tdsForm = tdsFormMap.get(user.id) || {};
        const tdsRefundOtp = tdsRefundOtpMap.get(user.id) || {};
        const tdsYear = tdsYearMap.get(user.id) || {};

        const yearData = {};
        let totalTds = 0;

        YEAR_OPTIONS.forEach(({ firestoreKey }) => {
          const rows = tdsDetails[firestoreKey] || [];
          yearData[firestoreKey] = rows;
          totalTds += rows.reduce((sum, item) => sum + Number(item.tds || 0), 0);
        });

        return {
          ...user,
          tdsState,
          ...tdsForm,
          tdsDetails,
          tdsYear,
          tdsRefundOtp,
          ...yearData,
          totalTds,
        };
      });
  // Sort data by timestamp, latest first
  const sortedData = mergedData.sort((a, b) => {
    const timestampA = a.timestamp ? new Date(a.timestamp) : new Date(0);
    const timestampB = b.timestamp ? new Date(b.timestamp) : new Date(0);
    return timestampB - timestampA;
  });

  setUsers(sortedData);
  
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 300000); // Refresh every 1 minute
    return () => clearInterval(interval); // Cleanup interval on component unmount
  }, []);

  const sendNotification = async (title, message, fcmToken) => {
    try {
      await axios.post('https://moneycartadmin.onrender.com/send-notification', {
        title,
        message,
        fcmToken,
      });
    } catch (error) {
      console.error('Error sending notification:', error);
    }
  };

  const handleEnableOtp = async (userId) => {
    try {
      const firestore = getFirestore(app);
      const userRef = doc(firestore, 'tds_state', userId);
      await updateDoc(userRef, { enableOtp: true });
      const updatedUsers = users.map(user => user.id === userId ? { ...user, tdsState: { ...user.tdsState, enableOtp: true } } : user);
      setUsers(updatedUsers);

      // Find the user to get the FCM token
      const user = users.find(user => user.id === userId);
      if (user) {
        // Send notification
        await sendNotification(
          'e-verified OTP',
          'Your ITR has been filed and it now needs to be e-verified to be submitted to the income tax department. Please enter the OTP received on your mobile number linked to your Aadhaar card.',
          user.fcmToken // Assuming you have FCM token stored in the user object
        );
      }
    } catch (error) {
      console.error('Error enabling OTP:', error);
    }
  };

  const handleFinalOtpStatus = async (userId, status) => {
    try {
      const firestore = getFirestore(app);
      const userRef = doc(firestore, 'tds_state', userId);
      await updateDoc(userRef, { finalotpstatus: status === 'true' });
      const updatedUsers = users.map(user => user.id === userId ? { ...user, tdsState: { ...user.tdsState, finalotpstatus: status === 'true' } } : user);
      setUsers(updatedUsers);
    } catch (error) {
      console.error('Error updating finalotpstatus:', error);
    }
  };

  const filteredUsers = users.filter(user => {
    // 1. Only show customers who have paid
    const isPaid = user.tdsState?.paymentstatus === true;
    if (!isPaid) return false;

    // 2. Search query check
    const matchesSearch = 
      (user.name && user.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (user.email && user.email.toLowerCase().includes(searchTerm.toLowerCase()));
    if (!matchesSearch) return false;

    // 3. Hide completed records unless showCompleted checkbox is checked
    if (!showCompleted && user.tdsState?.finalotpstatus === true) {
      return false;
    }

    return true;
  });

  return (
    <div>
      <NavbarDashboard />
      <Box height={10} />
      <Box sx={{ display: 'flex' }}>
        <ResponsiveDrawer userPermissions={userPermissions} />
        <Box component="main" sx={{ flexGrow: 1, p: 9 }}>
          <div className="container">
            <div className="search-bar" style={{ display: 'flex', alignItems: 'center', gap: '15px', flexWrap: 'wrap' }}>
              <input
                type="text"
                placeholder="Search by username or email..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
              <Button
                variant="contained"
                onClick={fetchData}
                style={{ backgroundColor: 'lightgreen' }}
                startIcon={<RefreshIcon />}
              >
                Refresh
              </Button>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontWeight: '500', marginLeft: '10px' }}>
                <input
                  type="checkbox"
                  checked={showCompleted}
                  onChange={(e) => setShowCompleted(e.target.checked)}
                  style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                />
                Show Completed Records
              </label>
            </div>

            <div className="table-wrapper">
              <table className="responsive-table">
                <thead>
                  <tr className="sticky-header">
                    <th>Sr No.</th>
                    <th>Date & Time</th>
                    <th>User Name</th>
                    <th>Email ID</th>
                    <th>Phone No.</th>
                    <th>Payment Status</th>
                    <th>Payment Confirmation</th>
                    {/* {YEAR_OPTIONS.map(option => (
                      <th key={option.value}>TDS {option.label}</th>
                    ))}
                    <th>User selected Previous Year TDS</th>
                    <th>Total TDS</th> */}
                    <th>Enable OTP</th>
                    <th>OTP</th>
                    <th>E-Verification Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((user, index) => (
                    <tr key={user.id} className={index % 2 === 0 ? 'even' : 'odd'}>
                      <td>{index + 1}</td>
                      <td>{user.timestamp ? new Date(user.timestamp).toLocaleString() : 'NA'}</td>
                      <td>{user.name}</td>
                      <td>{user.email}</td>
                      <td>{user.phone}</td>
                      <td className={user.tdsState.paymentstatus ? 'Complete' : 'Incomplete'}>
                        {user.tdsState.paymentstatus ? 'Complete' : 'Incomplete'}
                      </td>
                      <td className={user.tdsState.paymentConfirmation === 'true' ? 'Complete' : 'Incomplete'}>
                        {user.tdsState.paymentConfirmation === 'true' ? 'Complete' : 'Incomplete'}
                      </td>
                      {/* {YEAR_OPTIONS.map(option => (
                        <td key={option.value}>
                          {(user[option.firestoreKey] || []).length > 0
                            ? user[option.firestoreKey].map((tds, idx) => (
                                <div key={idx}>{`TDS: ${tds.tds}`}</div>
                              ))
                            : 'NA'}
                        </td>
                      ))}
                      <td>{formatSelectedPreviousYear(user.tdsYear)}</td>
                      <td>{user.totalTds}</td> */}
                      <td>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={() => handleEnableOtp(user.id)}
                          disabled={user.tdsState.enableOtp}
                        >
                          {user.tdsState.enableOtp ? 'Enabled' : 'Enable OTP'}
                        </Button>
                      </td>
                      <td>{user.tdsRefundOtp.otp || 'N/A'}</td>
                      <td>
                        <Select
                          style={{ color: 'white', backgroundColor: user.tdsState.finalotpstatus ? '#03C03C' : '#EF0107' }}
                          value={user.tdsState.finalotpstatus ? 'true' : 'false'}
                          onChange={(e) => handleFinalOtpStatus(user.id, e.target.value)}
                        >
                          <MenuItem value="true">Complete</MenuItem>
                          <MenuItem value="false">Incomplete</MenuItem>
                        </Select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </Box>
      </Box>
    </div>
  );
};

export default FinalOtp;

// CSS styles for the table
const css = `
  .Incomplete {
    background-color: #EF0107;
    color: white;
    border-style: solid;
  }

  .Complete, .Passed, .successfully_apply {
    background-color: #03C03C;
    color: white;
    border-style: solid;
  }
    
  .sticky-header {
    position: sticky;
    top: 0;
    background-color: #ffffff;
    z-index: 10;
  }

  .Failed, .incomplete {
    background-color: #EF0107;
    color: white;
    border-style: solid;
  }

  .even {
    background-color: #f2f2f2;
  }

  .odd {
    background-color: #ffffff;
  }
`;

document.head.insertAdjacentHTML('beforeend', `<style>${css}</style>`);
